import React, { useRef, useState, useEffect } from "react";
import Slider from "react-slick";
import { FaTrashAlt } from "react-icons/fa";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

const GalleryCards = () => {
    const [images, setImages] = useState([]);
    const [userId, setUserId] = useState(null);
    const [aboutUsId, setAboutUsId] = useState(null);
    const [aboutUsText, setAboutUsText] = useState("");
    const [missionText, setMissionText] = useState("");
    const [visionText, setVisionText] = useState("");
    const [vendorId, setVendorId] = useState(null);

    const fileInputRef = useRef(null);

    useEffect(() => {
        const storedVendorId = localStorage.getItem("vendorId");
        setVendorId(storedVendorId);
    }, []);

    useEffect(() => {
        const storedUserId = localStorage.getItem("userId");
        if (storedUserId) {
            setUserId(storedUserId);
        }
    }, []);

    useEffect(() => {
        const fetchAboutUsData = async () => {
            if (vendorId) {
                const token = localStorage.getItem("authToken");
                if (!token) {
                    alert("Authorization token missing. Please log in again.");
                    return;
                }

                try {
                    const response = await fetch(
                        `https://backend.maamaas.com/food/aboutus/${vendorId}`,
                        {
                            method: "GET",
                            headers: {
                                "Authorization": `Bearer ${token}`,
                            },
                        }
                    );

                    if (!response.ok) {
                        if (response.status === 404) {
                            alert("No 'About Us' information found for this user.");
                        }
                        return;
                    }

                    const responseData = await response.json();
                    const galleryData = Array.isArray(responseData) ? responseData[0] : responseData;

                    setAboutUsId(galleryData.aboutUsId || null);
                    setAboutUsText(galleryData.aboutUs || "");
                    setMissionText(galleryData.mission || "");
                    setVisionText(galleryData.vision || "");

                    const fetchedImagesArray = [];
                    const imageFieldsToDisplay = ['image1', 'image2', 'image3', 'image4'];

                    for (const field of imageFieldsToDisplay) {
                        if (galleryData[field] && typeof galleryData[field] === 'string' && galleryData[field].trim() !== "") {
                            let imgSrc = galleryData[field];
                            if (!imgSrc.startsWith('data:image')) {
                                imgSrc = `data:image/jpeg;base64,${imgSrc}`;
                            }
                            fetchedImagesArray.push({ base64: imgSrc });
                        }
                    }

                    setImages(fetchedImagesArray);
                } catch (error) {
                    console.error("Error fetching data:", error);
                }
            }
        };

        fetchAboutUsData();
    }, [vendorId]);

    const settings = {
        dots: false,
        infinite: images.length > 2,
        speed: 500,
        slidesToShow: Math.min(images.length, 3),
        slidesToScroll: 1,
        autoplay: images.length > 0,
        autoplaySpeed: 2000,
        pauseOnHover: true,
        responsive: [
            {
                breakpoint: 1024,
                settings: {
                    slidesToShow: Math.min(images.length, 2),
                    infinite: images.length > 1,
                },
            },
            {
                breakpoint: 768,
                settings: {
                    slidesToShow: Math.min(images.length, 1),
                    infinite: images.length > 0,
                },
            },
        ],
    };

    const handleAddImageClick = () => {
        if (!vendorId) {
            alert("User is not identified. Please log in.");
            return;
        }
        if (!aboutUsId) {
            alert("Cannot add images: 'About Us' record not found.");
            return;
        }
        if (images.length >= 4) {
            alert("Maximum 4 images allowed.");
            return;
        }
        fileInputRef.current.click();
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file && file.type.startsWith("image/")) {
            const reader = new FileReader();
            reader.onloadend = () => {
                const base64StringWithDataURI = reader.result;
                const updatedImages = [...images, { base64: base64StringWithDataURI }];
                setImages(updatedImages);
                if (vendorId && aboutUsId) {
                    sendImagesToAPI(updatedImages);
                }
            };
            reader.readAsDataURL(file);
        }
    };

    const handleDelete = (index) => {
        const updatedImages = images.filter((_, i) => i !== index);
        setImages(updatedImages);
        if (vendorId && aboutUsId) {
            sendImagesToAPI(updatedImages);
        }
    };

    const sendImagesToAPI = async (currentImagesArray) => {
        if (!vendorId || !aboutUsId) return;

        const token = localStorage.getItem("authToken");
        if (!token) {
            alert("Authorization token missing. Please log in again.");
            return;
        }

        const cleanBase64 = (str) => (str && str.includes(",")) ? str.split(",")[1] : str;
        const base64PayloadImages = currentImagesArray.map(img => cleanBase64(img?.base64 || null));

        const payload = {
            aboutUsId: aboutUsId,
            aboutUs: aboutUsText,
            image1: base64PayloadImages[0] || null,
            image2: base64PayloadImages[1] || null,
            image3: base64PayloadImages[2] || null,
            image4: base64PayloadImages[3] || null,
            userId: vendorId,
            mission: missionText,
            vision: visionText
        };

        try {
            const response = await fetch(
                `https://backend.maamaas.com/food/aboutus/gopi/${aboutUsId}`,
                {
                    method: "PUT",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${token}`
                    },
                    body: JSON.stringify(payload),
                }
            );

            if (response.ok) {
                alert("Content updated successfully!");
            } else {
                alert("Update failed.");
            }
        } catch (err) {
            console.error("Error updating:", err);
            alert("An error occurred while updating.");
        }
    };

    const ImageCard = ({ src, index }) => (
        <div className="image-card-container">
            <img
                src={src}
                alt={`gallery-item-${index}`}
                className="gallery-image"
            />
            <button
                onClick={(e) => { e.stopPropagation(); handleDelete(index); }}
                className="delete-button"
                title="Delete image"
            >
                <FaTrashAlt size={14} color="red" />
            </button>
        </div>
    );

    return (
        <section className="gallery-section">
            <div className="gallery-header">
                <h1 className="gallery-title">
                    <span className="title-red">Gall</span>
                    <span className="title-black">ery</span>
                </h1>

                <button
                    onClick={handleAddImageClick}
                    disabled={images.length >= 4 || !vendorId || !aboutUsId}
                    className="add-button"
                    title={
                        !vendorId ? "Login to manage gallery" :
                            !aboutUsId ? "About Us record not found" :
                                images.length >= 4 ? "Maximum 4 images allowed" :
                                    "Add image to gallery"
                    }
                >
                    Add+
                </button>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="file-input"
                />
            </div>

            {vendorId ? (
                aboutUsId ? (
                    images.length > 0 ? (
                        <Slider key={images.length} {...settings}>
                            {images.map((img, idx) => (
                                <div key={idx} className="slider-item">
                                    <ImageCard src={img.base64} index={idx} />
                                </div>
                            ))}
                        </Slider>
                    ) : (
                        <p className="empty-message">
                            No gallery images uploaded yet. Click "Add+" to upload.
                        </p>
                    )
                ) : (
                    <p className="empty-message">
                        Loading 'About Us' details or no record found.
                    </p>
                )
            ) : (
                <p className="empty-message">
                    Please log in to view or manage the gallery.
                </p>
            )}

            <style jsx>{`
                .gallery-section {
                    width: 100%;
                    max-width: 1200px;
                    margin: 0 auto;
                    padding: 20px 15px;
                }
                
                .gallery-header {
                    display: flex;
                    align-items: center;
                    margin-bottom: 20px;
                    justify-content: center;
                    flex-wrap: wrap;
                    gap: 15px;
                }
                
                .gallery-title {
                    font-size: 38px;
                    font-weight: bold;
                    letter-spacing: 1px;
                    text-transform: uppercase;
                    text-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
                    margin: 0;
                    text-align: center;
                }
                
                .title-red { color: red; }
                .title-black { color: black; }
                
                .add-button {
                    height: 35px;
                    border-radius: 5px;
                    border: none;
                    background: #b15cd6;
                    color: #fff;
                    font-weight: bold;
                    cursor: pointer;
                    width: 80px;
                    transition: all 0.3s ease;
                }
                
                .add-button:disabled {
                    background: #ccc;
                    cursor: not-allowed;
                }
                
                .add-button:hover:not(:disabled) {
                    background: #9a4cbf;
                }
                
                .file-input { display: none; }
                
                .image-card-container {
                    position: relative;
                    width: 100%;
                    height: 300px;
                    overflow: hidden;
                    border-radius: 16px;
                    margin: 0 10px;
                }
                
                .gallery-image {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                }
                
                .delete-button {
                    position: absolute;
                    top: 10px;
                    right: 10px;
                    background: rgba(0,0,0,0.6);
                    border: none;
                    border-radius: 50%;
                    padding: 6px;
                    color: white;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 2;
                }
                
                .empty-message {
                    text-align: center;
                    color: #777;
                    font-style: italic;
                    padding: 20px;
                }
                
                /* Responsive styles */
                @media (max-width: 992px) {
                    .gallery-title { font-size: 32px; }
                    .image-card-container { height: 280px; }
                }
                
                @media (max-width: 768px) {
                    .gallery-section { padding: 20px 10px; }
                    .gallery-title { font-size: 28px; }
                    .image-card-container { height: 250px; margin: 0 5px; }
                }
                
                @media (max-width: 480px) {
                    .gallery-header { flex-direction: column; gap: 10px; }
                    .gallery-title { font-size: 24px; }
                    .add-button { margin-left: 0; }
                    .image-card-container { height: 220px; }
                }
                
                @media (max-width: 320px) {
                    .gallery-title { font-size: 22px; }
                    .image-card-container { height: 200px; }
                }
            `}</style>
        </section>
    );
};

export default GalleryCards;